import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import dummyUserProfilePicture from '../assets/dummy.png';
import { useGlobalContext } from '../Contexts/GlobalContext';
import MiratsQuantoLogo from '../assets/logos/MiratsQuantoLogo.svg';
import { IoMdHelpCircleOutline } from 'react-icons/io';
import { Tooltip } from '@mui/material';
import MiratsAnimatedLogo from './MiratsAnimatedLogo';
// MiratsLogo component remains exactly as you had it

const Navbar = () => {
  const navigate = useNavigate();
  const { user } = useGlobalContext();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navData = [
    { name: 'Dashboard', path: '/' },
    { name: 'Surveys', path: '/surveys' },
    { name: 'Suppliers', path: '/suppliers' },
    { name: 'Survey Groups', path: '/survey-groups' },
    { name: 'Invoices & Billing', path: '/invoice-billing' },
    { name: 'Feasibility', path: '/feasibility' },
    { name: 'Qualification Library', path: '/qualification-library' },
  ];

  const hideNav = [
    'sign-up',
    'login',
    'DIY',
    'error',
    'verify-change',
    'reset-password',
    'payment',
    'onboarding',
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!Object.keys(user || {})?.length) return null;
  if (hideNav.some((data) => window.location.href.includes(data))) return null;

  return (
    <nav className="w-full print:hidden bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm transition-all">
      <div className="mx-auto w-[94%] max-w-[1600px] flex items-center justify-between h-[64px]">
        {/* Left: Logo Section */}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <MiratsAnimatedLogo />
          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden hidden sm:block ${isScrolled ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100'}`}
          >
            <img
              src={MiratsQuantoLogo}
              alt="miratsLogo"
              className="h-[32px] md:h-[38px] block"
            />
          </div>
        </div>

        {/* Center: Desktop Navigation Links */}
        <div className="hidden lg:flex items-center gap-1">
          {navData?.map((data, ind) => (
            <NavLink
              key={ind}
              to={data.path}
              className={({ isActive }) =>
                `relative px-3 py-2 text-[13px] xl:text-[14px] font-[500] text-gray-700 rounded-md transition-all hover:text-[#0226BE] hover:bg-blue-50 ${isActive ? 'text-[#0226BE] font-[600] bg-blue-50' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{data.name}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0226BE] rounded-full"></span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Right: User Profile + Mobile Toggle */}
        <div className="flex items-center gap-2 sm:gap-4 cursor-pointer">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/profile/my-profile')}
          >
            <div className="hidden md:flex flex-col items-end">
              <h1 className="text-[13px] font-[600] text-[#1a1a1a] truncate max-w-[120px]">
                {user?.personalDetails?.fullName}
              </h1>
              <p className="text-[11px] text-gray-500 truncate max-w-[120px]">
                {user?.email}
              </p>
            </div>

            <img
              src={user?.avatar ? user?.avatar : dummyUserProfilePicture}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = dummyUserProfilePicture;
              }}
              alt="user profile"
              className="w-[44px] h-[44px] rounded-full object-cover border-2 border-gray-200 group-hover:border-[#0226BE] transition-colors"
            />
          </div>
          <Tooltip
            title="Help"
            arrow
            placement="bottom"
            className="lg:block hidden "
          >
            <button
              type="button"
              className="flex items-center justify-center focus:outline-none"
              onClick={() => navigate('/help-center')}
            >
              <IoMdHelpCircleOutline className="w-8 h-8 text-gray-500 cursor-pointer" />
            </button>
          </Tooltip>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {menuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Drawer (Collapsible) */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden bg-white border-t ${menuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="flex flex-col p-4 gap-2">
          {navData?.map((data, ind) => (
            <React.Fragment key={ind}>
              <NavLink
                key={ind}
                to={data.path}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-[14px] ${isActive ? 'bg-blue-50 text-[#0226BE] font-bold' : 'text-gray-600'}`
                }
              >
                {data.name}
              </NavLink>
            </React.Fragment>
          ))}
          <NavLink
            to={'/help-center'}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `px-4 py-3 rounded-md text-[14px] flex items-center gap-1 ${isActive ? 'bg-blue-50 text-[#0226BE] font-bold' : 'text-gray-600'}`
            }
          >
            <IoMdHelpCircleOutline className="w-6 h-6 text-gray-500 cursor-pointer" />{' '}
            Help
          </NavLink>
          <hr className="my-2" />
          <div className="px-4 py-2 md:hidden">
            <p className="text-[14px] font-bold">
              {user?.personalDetails?.fullName}
            </p>
            <p className="text-[12px] text-gray-500">{user?.email}</p>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
